import { computed, unref } from 'vue'
import { useBillingStore } from '@/stores/BillingStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { formatDate } from '@/helpers/times'

/**
 * Everything the "change your plan" surfaces need to describe one plan change:
 * which direction it goes, what it costs today, and when the next bill lands.
 *
 * This used to live twice, copy-pasted into `UpgradeFeatureModal` and
 * `PlanSection` (which render the SAME dialog), with a third variant of the
 * date math in `Settings/Plans`. The copies had drifted, and all three of the
 * bugs reported for monthly→annual All-Star came out of that:
 *
 *   • The verb came from `currentPlan.id < targetPlan.id`. A monthly→annual
 *     move keeps the same plan id, so the comparison was false and the dialog
 *     said "Downgrade to All-Star" for what is plainly not a downgrade. The
 *     billing PERIOD was not part of the comparison at all.
 *   • "Total due today" read the CURRENT plan's price at the TARGET period —
 *     the annual price of the plan the user is paying monthly for. Monthly
 *     All-Star ($59/mo) → Annual All-Star ($588/yr) compared 588 against 588,
 *     so the difference was 0 and the dialog promised "$0.00" due today while
 *     Stripe charged the full year.
 *   • The next-billing date was the current period's end + 1 day ("Oct 2"),
 *     which describes a change that waits for the cycle to roll over. A period
 *     switch does not wait: the old subscription ends, a new one starts today,
 *     and the next bill is one full period from now (Stripe: Sep 16 2027).
 *     `Settings/Plans` had the same expression with `- 1` instead of `+ 1`, so
 *     it rendered the renewal a day EARLIER than Stripe.
 */

export const PLAN_CHANGE = {
  UPGRADE: 'upgrade',
  DOWNGRADE: 'downgrade',
  // Same plan, different billing period. Neither "upgrade" nor "downgrade"
  // describes it, and forcing one of them is what this ticket was about.
  SWITCH: 'switch',
}

const ACTION_LABELS = {
  [PLAN_CHANGE.UPGRADE]: 'Upgrade',
  [PLAN_CHANGE.DOWNGRADE]: 'Downgrade',
  [PLAN_CHANGE.SWITCH]: 'Switch',
}

const MS_PER_DAY = 1000 * 60 * 60 * 24
const DAYS_PER_YEAR = 365

/** A subscription in its trial window — no money has moved yet. */
export const isSubscriptionOnTrial = (subscription) => !!(
  subscription &&
  subscription.trial_start &&
  subscription.stripe_status === 'trialing' &&
  !subscription.complimentary &&
  new Date() < new Date(subscription.expiration_date)
)

const atMidnight = (value) => {
  const date = new Date(value)
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * @param {import('vue').Ref|object} targetPlan the plan the user is moving TO
 */
export function usePlanChange (targetPlan) {
  const billingStore = useBillingStore()
  const configStore = useConfigStore()

  const target = computed(() => unref(targetPlan) || {})
  const subscription = computed(() => configStore.config?.subscription)
  const isActiveSubscription = computed(() => !!configStore.getConfigField('hasActiveSubscription'))
  const isOnTrial = computed(() => isSubscriptionOnTrial(subscription.value))

  // The period being paid for today, and the one the user is moving to. The
  // upgrade dialog follows the Annual/Monthly toggle; a feature gate has no
  // toggle, so it stays on whatever the subscription already bills at.
  const currentIsAnnual = computed(() => !!subscription.value?.annual)
  const targetIsAnnual = computed(() =>
    billingStore.upgradeFeatureModalData?.isPlanUpgradeModal
      ? billingStore.plansPeriods.selected === 'annual'
      : currentIsAnnual.value
  )

  const periodLabel = computed(() => targetIsAnnual.value ? 'year' : 'mo')

  // A period switch ends the current subscription and starts a new one today,
  // rather than amending the running one — which is why it bills immediately
  // and re-anchors the cycle. A tier change inside the same period does not.
  const isPeriodSwitch = computed(
    () => isActiveSubscription.value && targetIsAnnual.value !== currentIsAnnual.value
  )

  const direction = computed(() => {
    if (!isActiveSubscription.value) return PLAN_CHANGE.UPGRADE

    const currentTier = billingStore.currentPlan?.id ?? 0
    const targetTier = target.value?.id ?? 0

    if (targetTier > currentTier) return PLAN_CHANGE.UPGRADE
    if (targetTier < currentTier) return PLAN_CHANGE.DOWNGRADE

    return PLAN_CHANGE.SWITCH
  })

  const actionLabel = computed(() => ACTION_LABELS[direction.value])

  // On a period switch the period is the whole point of the change, so it
  // belongs in the heading: "Switch to Annual All-Star", not "Switch to
  // All-Star" (which reads as a no-op next to the plan they already have).
  const targetPlanTitle = computed(() => {
    const label = target.value?.label || ''

    if (!isPeriodSwitch.value) return label

    return `${targetIsAnnual.value ? 'Annual' : 'Monthly'} ${label}`.trim()
  })

  /** Full price of one period of the target plan. */
  const planCost = computed(() => targetIsAnnual.value
    ? (target.value?.price?.annual_final || 0)
    : (target.value?.price?.monthly || 0)
  )

  /** Full price of one period of what the user pays TODAY, at today's period. */
  const currentCost = computed(() => currentIsAnnual.value
    ? (billingStore.currentPlan?.price?.annual_final || 0)
    : (billingStore.currentPlan?.price?.monthly || 0)
  )

  // Days left in the period already paid for, and how long that period runs.
  const daysRemaining = computed(() => {
    const expiration = subscription.value?.expiration_date
    if (!expiration) return 0

    const expDate = atMidnight(expiration)

    return Math.max(1, Math.round((expDate - atMidnight(new Date())) / MS_PER_DAY) + 1)
  })

  const daysInCurrentPeriod = computed(() => {
    if (currentIsAnnual.value) return DAYS_PER_YEAR

    const expiration = subscription.value?.expiration_date
    if (!expiration) return 30

    const expDate = atMidnight(expiration)

    return new Date(expDate.getFullYear(), expDate.getMonth() + 1, 0).getDate()
  })

  const dueToday = computed(() => {
    // Nothing running yet, or a trial ending on the spot: a full period starts
    // now, so the full price is charged now.
    if (!isActiveSubscription.value || isOnTrial.value) return planCost.value

    // New subscription starting today — the whole first period is billed. We
    // deliberately do NOT net off the unused days of the period being left;
    // that credit is Stripe's to apply and the invoice we bill is the full
    // period. Promising a discount the invoice does not show is how this said
    // "$0.00" and charged a full year.
    if (isPeriodSwitch.value) return planCost.value

    // Same period, different tier: the running cycle continues and only the
    // difference is owed, for the days still left in it.
    const difference = Math.max(0, planCost.value - currentCost.value)
    if (!difference) return 0
    if (!subscription.value?.expiration_date) return difference

    return (daysRemaining.value / daysInCurrentPeriod.value) * difference
  })

  const nextBillingDate = computed(() => {
    const next = new Date()

    // Trial or period switch: the cycle is re-anchored to today, so the next
    // bill is one full target period out.
    if (isOnTrial.value || isPeriodSwitch.value || !isActiveSubscription.value) {
      targetIsAnnual.value
        ? next.setFullYear(next.getFullYear() + 1)
        : next.setMonth(next.getMonth() + 1)

      return formatDate(next)
    }

    // Cycle continues: `expiration_date` IS the next renewal (Stripe's
    // `current_period_end`), so show it as-is. The ± 1 day the old copies
    // applied put this a day off in both directions.
    return formatDate(subscription.value?.expiration_date || next)
  })

  return {
    isActiveSubscription,
    isOnTrial,
    currentIsAnnual,
    targetIsAnnual,
    periodLabel,
    isPeriodSwitch,
    direction,
    actionLabel,
    targetPlanTitle,
    planCost,
    currentCost,
    dueToday,
    nextBillingDate,
  }
}
